import {
  Card,
  Grid,
  CrudMode,
  Stack,
  IconButton,
  Tooltip,
  Typography,
  Box,
  List,
  deepCopy,
  RichTextReader,
  RichTextEditor,
  DeleteIconButton,
  DELETE_WARNING,
  GridBreak,
  ValidationErrors,
} from "@compass/core";
import { Form, FormQuestion, FormSection, SurveyEditResource, SurveyViewResource } from "generated/openApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { useEffect, useRef, useState } from "react";
import { createEntity } from "utils/CreateEntity";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { MyDroppable } from "components/common/Draggable/Droppable";
import { reorder } from "utils/Reorder";
import { useNavigate } from "react-router-dom";

// TODO REFACTOR

type SectionQuestions = {
  displayOrder: number;
  formSection: FormSection;
  formQuestions: FormQuestion[];
};

type DragMoveResult<T> = {
  sourceList: DroppableUpdate<T>;
  destinationList: DroppableUpdate<T>;
};

type DroppableUpdate<T> = {
  droppableId: string;
  listItems: T[];
};

export const SurveySections = (props: {
  survey: Form;
  setSurvey: (value: Form) => void;
  copySurvey: number | undefined;
  crudMode: CrudMode;
  refreshSurvey: Form | undefined;
  validationErrors: ValidationErrors;
}) => {
  const [sectionQuestions, setSectionQuestions] = useState<SectionQuestions[]>([]);
  const [selectedDisplayOrder, setSelectedDisplayOrder] = useState<number>();
  const syncingSectionsFromSurvey = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    syncingSectionsFromSurvey.current = true;
    setSectionQuestions(updateQuestionDisplayOrder());
  }, [props.copySurvey, props.refreshSurvey]);

  useEffect(() => {
    if (syncingSectionsFromSurvey.current) {
      syncingSectionsFromSurvey.current = false;
      return;
    }

    let tempQuestions: FormQuestion[] = [];
    let tempSections: FormSection[] = [];

    // when moving sections, we need to update each of their child elements (i.e. questions) as well
    // so the display orders are consistent.

    // if we don't have any sections, don't do this (for the blank section)
    if (sectionQuestions.length == 0) return;

    var tempSectionQuestions = deepCopy(sectionQuestions);
    var displayOrder = 1;
    tempSectionQuestions.forEach((sq) => {
      sq.formSection.displayOrder = displayOrder;
      sq.formQuestions.forEach((q) => {
        q.displayOrder = displayOrder;
        displayOrder++;
      });

      if (sq.formQuestions.length == 0) displayOrder++;
    });

    tempSectionQuestions.map((sq) => {
      tempQuestions = tempQuestions.concat(sq.formQuestions);
      tempSections.push(sq.formSection);
    });

    props.setSurvey(
      produce(props.survey, (draft) => {
        draft!.formQuestions = tempQuestions;
        draft!.formSections = tempSections;
      })
    );
  }, [sectionQuestions]);

  const updateQuestionDisplayOrder = (): SectionQuestions[] => {
    let temp: SectionQuestions[] = [];

    var formSectionsCopy = deepCopy(props.survey.formSections);
    var tempSections = formSectionsCopy?.sort((a, b) => a.displayOrder! - b.displayOrder!);

    tempSections?.forEach((formSection, index) => {
      // get form questions that are the same display order, or higher (until we reach the next section if it exists)
      const minDisplayOrder = formSection.displayOrder!;
      const nextSection = tempSections![index + 1];
      const maxDisplayOrder = nextSection ? nextSection.displayOrder! : 9999;

      temp.push({
        formSection,
        displayOrder: index + 1,
        formQuestions: props.survey.formQuestions
          ? props.survey.formQuestions.filter(
              (q) => q.displayOrder >= minDisplayOrder && q.displayOrder < maxDisplayOrder
            )
          : [],
      });
    });

    return temp;
  };

  /**
   * Moves an item from one list to another list.
   */
  const move = (
    source: FormQuestion[],
    destination: FormQuestion[],
    droppableSource,
    droppableDestination
  ): DragMoveResult<FormQuestion> => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result: DragMoveResult<FormQuestion> = {
      sourceList: {
        droppableId: droppableSource.droppableId,
        listItems: sourceClone,
      },
      destinationList: {
        droppableId: droppableDestination.droppableId,
        listItems: destClone,
      },
    };

    return result;
  };

  const getQuestionDraggableId = (question: FormQuestion) =>
    question.id > 0 ? question.id.toString() : `question:${question.tempId || question.displayOrder}`;

  const getSectionDroppableId = (section: FormSection) =>
    section.id > 0 ? section.id.toString() : `section:${section.tempId || section.displayOrder}`;

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sID = source.droppableId;
    const dID = destination.droppableId;

    if (sID === dID) {
      // source droppable is the same as the destination, so it is moving within the same container
      // just need to reorder the items in the container
      if (type == "sections") {
        const tempSectionQuestions = reorder<SectionQuestions>(sectionQuestions, source.index, destination.index);

        setSectionQuestions(tempSectionQuestions);
      } else {
        let questions: FormQuestion[];
        if (sID === "blank") {
          questions = props.survey.formQuestions!;
          const items = reorder<FormQuestion>(questions, source.index, destination.index);
          props.setSurvey(
            produce(props.survey, (draft) => {
              draft!.formQuestions = items;
            })
          );
        } else {
          questions = sectionQuestions.find((s) => getSectionDroppableId(s.formSection) == sID)?.formQuestions || [];
          const items = reorder<FormQuestion>(questions, source.index, destination.index);
          setSectionQuestions(
            produce((draft) => {
              let index = draft.findIndex((s) => getSectionDroppableId(s.formSection) == sID);

              draft[index].formQuestions = items;
            })
          );
        }
      }
    } else {
      if (type == "sections") {
        return;
      } else {
        var sourceQuestions = sectionQuestions.find((s) => getSectionDroppableId(s.formSection) == sID)?.formQuestions || [];
        var destinationQuestions = sectionQuestions.find((s) => getSectionDroppableId(s.formSection) == dID)?.formQuestions || [];
        const result = move(sourceQuestions, destinationQuestions, source, destination);

        setSectionQuestions(
          produce((draft) => {
            let sourceIndex = draft.findIndex((s) => getSectionDroppableId(s.formSection) == result.sourceList.droppableId);
            draft[sourceIndex].formQuestions = result.sourceList.listItems;

            let destinationIndex = draft.findIndex((s) => getSectionDroppableId(s.formSection) == result.destinationList.droppableId);
            draft[destinationIndex].formQuestions = result.destinationList.listItems;
          })
        );
      }
    }
  };

  const grid = 8;

  const getItemStyle = (padding, isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * padding,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? "lightgreen" : "",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const addNewSection = (displayOrder?: number) => {
    const selectedIndex = sectionQuestions.findIndex((s) => s.displayOrder == (displayOrder || selectedDisplayOrder));
    // const selectedDisplayOrder = sectionQuestions[selectedIndex].displayOrder;

    setSectionQuestions(
      produce((draft) => {
        let newSectionQuestion: SectionQuestions = {
          formQuestions: sectionQuestions.length == 0 && props.survey.formQuestions ? props.survey.formQuestions : [],
          formSection: createEntity<FormSection>({ displayOrder: displayOrder || selectedDisplayOrder! + 1 }),
          displayOrder: selectedDisplayOrder! + 1,
        };

        draft!.splice(selectedIndex + 1, 0, newSectionQuestion);

        draft.forEach((sq, index) => {
          sq.displayOrder = index + 1;
        });
      })
    );

    setSelectedDisplayOrder(displayOrder || selectedDisplayOrder! + 1);
  };

  const deleteSection = (section, e) => {
    if (window.confirm(DELETE_WARNING)) {
      e.stopPropagation();
      setSectionQuestions(
        produce((draft) => {
          let sectionIndex = draft.findIndex((s) => s.displayOrder == section.displayOrder);

          draft[sectionIndex].formSection.deleted = true;
        })
      );
    }
  };

  const renderDraggable = (question: FormQuestion, index: number) => {
    return (
      <Draggable key={getQuestionDraggableId(question)} draggableId={getQuestionDraggableId(question)} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(0, snapshot.isDragging, provided.draggableProps.style)}
          >
            <Box sx={{ p: 2, border: "1px dashed grey" }}>
              <Grid {...provided.dragHandleProps} container spacing="lg">
                <Grid item xs={2}>
                  <Typography>{question.label}</Typography>
                </Grid>
                <Grid item xs={10}>
                  <RichTextReader label="Instructions" html={question.name} />
                </Grid>
              </Grid>
            </Box>
          </div>
        )}
      </Draggable>
    );
  };

  // if we have no sections, we still need a way to move questions around, so we create something that looks like
  // a section, but is really just all the questions
  const renderBlankSection = () => {
    return (
      <Card>
        <MyDroppable id={"blank"} type="questions">
          <Grid container spacing="lg">
            <Grid item xs={12}>
              <List>{props.survey.formQuestions?.map((s, i) => renderDraggable(s, i))}</List>
            </Grid>
          </Grid>
        </MyDroppable>
      </Card>
    );
  };

  const getValidationError = (index: number) => {
    const allSectionValidationErrors = props.validationErrors[`formSections`];
    let sectionValidationErrors = {};
    if (allSectionValidationErrors) {
      sectionValidationErrors = allSectionValidationErrors[index];
    }

    if (sectionValidationErrors?.[`instruction`]) return sectionValidationErrors?.[`instruction`];
    else if (sectionValidationErrors?.[`name`]) return sectionValidationErrors?.[`name`];
    return null;
  };

  const renderSections = (section: SectionQuestions, index: number) => {
    const sectionDroppableId = getSectionDroppableId(section.formSection);

    return (
      <Draggable key={sectionDroppableId} draggableId={sectionDroppableId} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(2, snapshot.isDragging, provided.draggableProps.style)}
          >
            <Grid container spacing="lg">
              <Grid item xs={10}>
                <Card onClick={() => setSelectedDisplayOrder(section.displayOrder)}>
                  <MyDroppable id={sectionDroppableId} type="questions">
                    <Grid container spacing="lg">
                      {getValidationError(index) != null && (
                        <Grid item xs={12}>
                          <Typography color="error">{getValidationError(index)}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} {...provided.dragHandleProps}>
                        {props.crudMode === "view" && <RichTextReader label="Label" html={section.formSection.name} />}
                        {props.crudMode === "edit" && (
                          <RichTextEditor
                            label="Label"
                            value={section.formSection.name}
                            onChange={(value) =>
                              setSectionQuestions(
                                produce((draft) => {
                                  let sectionIndex = draft.findIndex((s) => s.displayOrder == section.displayOrder);

                                  draft[sectionIndex].formSection.name = value!;
                                })
                              )
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        {props.crudMode === "view" && (
                          <RichTextReader label="Instructions" html={section.formSection.instruction} />
                        )}
                        {props.crudMode === "edit" && (
                          <RichTextEditor
                            label="Instructions"
                            value={section.formSection.instruction}
                            onChange={(value) =>
                              setSectionQuestions(
                                produce((draft) => {
                                  let sectionIndex = draft.findIndex((s) => s.displayOrder == section.displayOrder);

                                  draft[sectionIndex].formSection.instruction = value;
                                })
                              )
                            }
                          />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <List>{section.formQuestions.map((s, i) => renderDraggable(s, i))}</List>
                      </Grid>
                    </Grid>
                  </MyDroppable>
                </Card>
              </Grid>
              {props.crudMode == "edit" && section.displayOrder == selectedDisplayOrder && (
                <Grid item xs={2} display="flex" alignItems="center" justifyContent="center">
                  <Card sx={{ maxWidth: "100px" }}>
                    <Stack direction="column" spacing="md">
                      <IconButton onClick={() => addNewSection()}>
                        <Tooltip title="Add New Section">
                          <AddCircleOutlineIcon />
                        </Tooltip>
                      </IconButton>

                      <DeleteIconButton
                        onClick={(e) => {
                          deleteSection(section, e);
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              )}
            </Grid>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Grid container spacing="lg">
        <Grid item xs={11} display="flex" alignItems="center" justifyContent="center">
          <Grid container spacing="lg" direction="column">
            <Grid item md={10} xs={12}>
              <Card>
                <Typography>
                  Note: You can drag and drop both sections and questions here to alter the order in which they are
                  displayed in the survey.
                </Typography>
              </Card>
            </Grid>

            <GridBreak />

            <MyDroppable id="sections" type="sections">
              {sectionQuestions?.filter((sq) => !sq.formSection.deleted).map((s, i) => renderSections(s, i))}
              {props.survey &&
                (sectionQuestions.length == 0 || !sectionQuestions.some((x) => !x.formSection.deleted)) && (
                  <Stack direction="column" spacing="md" py={2}>
                    {renderBlankSection()}
                    <Grid item xs={10} display="flex" alignItems="center" justifyContent="center">
                      <Card>
                        {props.crudMode === "edit" && (
                          <Stack direction="row" spacing="md" py={2}>
                            <Typography>No sections yet, click to begin!</Typography>
                            <IconButton onClick={() => addNewSection(1)}>
                              <Tooltip title="Add New Section">
                                <AddCircleOutlineIcon />
                              </Tooltip>
                            </IconButton>
                          </Stack>
                        )}
                        {props.crudMode === "view" && (
                          <Stack direction="row" spacing="md" py={2}>
                            <Typography>No sections have been added</Typography>
                          </Stack>
                        )}
                      </Card>
                    </Grid>
                  </Stack>
                )}
            </MyDroppable>
          </Grid>
        </Grid>
      </Grid>
    </DragDropContext>
  );
};
